import React, { useEffect, useState } from "react";

// Cache SVG raw text to avoid re-fetching the same file
const svgCache = new Map();

/**
 * Loads an SVG file and renders it inline, with the ability to:
 * - override the fill colors of any CSS class (e.g. ".cls-3 { fill: #xxx; }")
 * - override the SVG's root viewBox to crop to a specific region (for use
 *   with multi-design sprite SVGs)
 */
export default function CustomizableSvg({ src, viewBox, colorOverrides, idOverrides, syntheticOverlays, className, style, dataTestId }) {
  const [svgHtml, setSvgHtml] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let raw = svgCache.get(src);
      if (!raw) {
        try {
          const res = await fetch(src);
          raw = await res.text();
          svgCache.set(src, raw);
        } catch {
          raw = "";
        }
      }
      if (cancelled) return;
      let out = raw;
      // Override the root viewBox (only the first occurrence on the <svg> root)
      if (viewBox) {
        out = out.replace(/viewBox="[^"]+"/, `viewBox="${viewBox}"`);
      }
      // Ensure the inline SVG fills its parent container and preserves aspect.
      // We FORCE width/height to 100% (replacing any built-in pixel sizing
      // from the source file, e.g. PyMuPDF emits width="413.836" which
      // would otherwise overflow our 72×72 thumbnail containers).
      out = out.replace(/<svg([^>]*)>/, (match, attrs) => {
        let a = attrs
          .replace(/\swidth="[^"]*"/g, "")
          .replace(/\sheight="[^"]*"/g, "");
        a += ' width="100%" height="100%"';
        if (!/preserveAspectRatio=/.test(a)) a += ' preserveAspectRatio="xMidYMid meet"';
        return `<svg${a}>`;
      });
      // Substitute fill colors for each `cls-N` class declared in the <style>.
      // We BOTH (a) rewrite the <style> rule (works for most browsers) and
      // (b) inject an inline `style="fill:..."` attribute on every path
      // bearing this class. The inline rule is required because some
      // browsers (notably Chromium under React `dangerouslySetInnerHTML`)
      // do not re-evaluate <style> rules added in the same DOM mutation
      // batch, leaving the new fill ignored on the freshly-inserted paths.
      if (colorOverrides) {
        for (const [cls, color] of Object.entries(colorOverrides)) {
          if (!color) continue;
          // (a) Patch the CSS rule (cosmetic — keeps the <style> consistent
          // with the visible state for any subsequent re-render).
          const styleRe = new RegExp(`(\\.${cls}\\s*\\{[^}]*fill\\s*:\\s*)#[0-9a-fA-F]{3,8}`, "g");
          out = out.replace(styleRe, `$1${color}`);
          // (b) Inject inline `style="fill:..."` (HIGHEST CSS specificity —
          // beats both class CSS in <style> and bare `fill` attribute). We
          // append the attribute right before the closing `>` of every
          // <path … class="…cls…"…>. Idempotent: removes any existing
          // `style="…fill…"` for this path before appending the new one.
          const cssEscapedCls = cls.replace(/([\\^$*+?.()|[\]{}])/g, "\\$1");
          const pathRe = new RegExp(
            `(<path[^>]*?\\bclass="[^"]*\\b${cssEscapedCls}\\b[^"]*"[^>]*?)(\\s*\\/?>)`,
            "g"
          );
          out = out.replace(pathRe, (m, head, tail) => {
            const cleaned = head
              .replace(/\s+style="[^"]*"/g, "")
              .replace(/\s+fill="[^"]*"/g, "");
            return `${cleaned} style="fill:${color}"${tail}`;
          });
        }
      }
      // Inject synthetic overlay <use> elements right before </svg>.
      // Each overlay clones a source path (by ID) scaled around its center.
      if (syntheticOverlays && syntheticOverlays.length > 0) {
        const overlaySvg = syntheticOverlays
          .map((ov) => {
            // Find the source path's bbox in the raw SVG to compute its center.
            // We rely on the source path's `d` attribute. Use a lazy approach:
            // wrap in a <g> with transform-origin set via SVG transform.
            const { id, sourceId, scale, color } = ov;
            if (!color || color === "transparent" || color === "__transparent__") return "";
            // Try to find source path's first M coordinate as a proxy for its
            // anchor (top-left of bbox); approximate center from viewBox.
            // We use SVG's `transform-box: fill-box` which lets us pivot at
            // the geometric center of the cloned shape.
            return `<use id="${id}" href="#${sourceId}" fill="${color}" style="transform-box:fill-box;transform-origin:center;transform:scale(${scale});" />`;
          })
          .join("");
        if (overlaySvg) {
          out = out.replace(/<\/svg>\s*$/, `${overlaySvg}</svg>`);
        }
      }
      // Override fill/stroke for specific element IDs by injecting CSS rules.
      // Special sentinel: a color value of "transparent" emits
      //   fill: transparent !important; fill-opacity: 0 !important;
      // so the underlying scene shows through (real "hollow" effect).
      // Rules target BOTH fill AND stroke so the same mechanism works for
      // - filled shapes whose fill comes from a CSS class (e.g. croix
      //   classiques: <polygon id="s2c1-e0" class="st0">),
      // - filled shapes with inline fill="..." (e.g. neon traits),
      // - outline-only shapes with fill="none" (we keep them hollow).
      if (idOverrides && Object.keys(idOverrides).length > 0) {
        const rules = Object.entries(idOverrides)
          .filter(([, color]) => !!color)
          .map(([id, color]) => {
            if (color === "transparent" || color === "__transparent__") {
              return `#${id}{fill:transparent !important;fill-opacity:0 !important;opacity:0 !important}`;
            }
            // Base rule (specificity 1,0,0): apply both fill and stroke.
            // Override rule (specificity 1,1,0) restores fill:none on shapes
            // that explicitly opt out of being filled.
            return `#${id}{fill:${color} !important;fill-opacity:1 !important;opacity:1 !important;stroke:${color} !important;color:${color}}` +
                   `#${id}[fill="none"]{fill:none !important}`;
          })
          .join("");
        if (rules) {
          // Inject inside the existing <style> block to keep one stylesheet
          if (/<style[^>]*>/.test(out)) {
            out = out.replace(/<style([^>]*)>/, `<style$1>${rules}`);
          } else {
            // Fallback: add a style block right after <svg ...>
            out = out.replace(/(<svg[^>]*>)/, `$1<style>${rules}</style>`);
          }
        }
      }
      // Strip prolog/DOCTYPE to keep raw <svg> only
      out = out.replace(/^<\?xml[^>]*\?>\s*/i, "").replace(/<!DOCTYPE[^>]*>\s*/i, "");
      setSvgHtml(out);
    };
    load();
    return () => { cancelled = true; };
  }, [src, viewBox, JSON.stringify(colorOverrides), JSON.stringify(idOverrides), JSON.stringify(syntheticOverlays)]);

  if (!svgHtml) return null;
  return (
    <div
      className={className}
      style={{ display: "inline-block", lineHeight: 0, width: "100%", height: "100%", ...style }}
      data-testid={dataTestId}
      // safe: we control these SVG files (in /public)
      dangerouslySetInnerHTML={{ __html: svgHtml }}
    />
  );
}
